import Phaser from 'phaser';
import { Pipe } from '../entities/Pipe';
import type { Port } from '../entities/Port';
import type { Organ } from '../entities/Organ';
import { classifyPipe, fluidClassFor, type MoleculeMix, type MoleculeType } from '../types';
import { FlowFx } from './FlowFx';
import { TracheaFlowFx } from './TracheaFlowFx';
import { COLORS } from '../colors';
import { gridToWorld } from '../grid';

const MOL_TEXTURE: Record<MoleculeType, string> = {
  food: 'mol-food',
  oxygen: 'mol-oxygen',
  co2: 'mol-co2',
  gruel: 'mol-gruel',
  waste: 'mol-waste',
  pulse: 'mol-pulse',
};

// Must match FlowFx — molecules travel at this rate.
const MOL_SPEED_PX_PER_MS = 0.18;

/**
 * Runs validation on all connected pipes when the user presses START FLUIDS.
 * - Matching (CORRECT) pipes flow indefinitely.
 * - ACCEPTABLE pipes flow (e.g. food-only into a brain that accepts food+oxygen)
 *   but don't count toward "solved" and don't trigger activation.
 * - UNACCEPTABLE pipes flow until the first molecule arrives at the wrong
 *   receptacle, then burst (X + blood spurt + dim).
 * - Spout↔spout pipes flow normally; the destination spout spurts when the first
 *   molecule arrives via the pipe (v5.0+ — used to burst immediately).
 * - Receptacle↔receptacle pipes are inert (nothing pushes).
 * - Unattached spouts spurt blood + their molecules.
 *
 * v5.0 organ rules: output spouts may declare `dependsOn: string[]`. Their
 * pipes start in `connected` (gray) and transition to `flowing` only once their
 * declared input dependencies are themselves flowing CORRECT. The per-frame
 * `update()` method drives that state machine — see its doc.
 */
export class Connector {
  private fxByPipeId = new Map<string, FlowFx>();
  private burstSpritesByPipeId = new Map<string, Phaser.GameObjects.GameObject[]>();
  private pendingBurstTimers = new Map<string, Phaser.Time.TimerEvent>();
  private pendingActivationTimers: Phaser.Time.TimerEvent[] = [];
  /** Timers for spout↔spout destination spurts and rule-triggered arrival effects. */
  private pendingArrivalTimers = new Map<string, Phaser.Time.TimerEvent>();
  private spoutSpurts: Phaser.GameObjects.GameObject[] = [];
  /** True between startFluids() and stopFluids() — gates the per-frame update. */
  private isFluidsRunning = false;
  /** Pipes that already have downstream effects (burst / arrival-spurt) scheduled. */
  private armedPipes = new Set<string>();
  /**
   * Pipe IDs whose first molecule has actually traveled the full pipe length and
   * arrived at the destination port. Driven by a `delayedCall(travelMs)` scheduled
   * when each pipe transitions to `flowing`. The per-frame dep eval requires this
   * — without it, downstream organ rules would fire immediately when their
   * upstream pipe ENTERED flowing state, before the molecule had visually reached
   * the input port. This is what gives the cascade its visual timing.
   */
  private deliveredPipes = new Set<string>();
  /**
   * Tracks cone-tip spurt particles per organ-output spout that has no pipe
   * attached. Keyed by spout port id. Started/stopped reactively by `update()`
   * when the spout's rule satisfies/unsatisfies.
   */
  private organSpoutSpurts = new Map<string, Phaser.GameObjects.GameObject[]>();
  /**
   * Last computed emission for each organ-output spout (those with `dependsOn`
   * or `transform`). Drives the per-frame state machine — when this value
   * changes between frames, the per-frame update reconciles the world
   * (pipe flow start/stop, mid-flow `setCarries`, cone-tip spurt etc).
   */
  private currentSpoutEmissions = new Map<string, MoleculeMix>();
  /** Active TracheaFlowFx instances by pipe id (one per trachea pipe). */
  private tracheaFxByPipeId = new Map<string, TracheaFlowFx>();
  /** Pending validIf-check timers by pipe id (parallel to pendingBurstTimers). */
  private pendingValidIfTimers = new Map<string, Phaser.Time.TimerEvent>();

  constructor(
    private scene: Phaser.Scene,
    private getPorts: () => Port[],
    private getPipes: () => Pipe[],
    private getOrgans: () => Organ[] = () => [],
    private getActivationPortId: () => string | undefined = () => undefined,
    /**
     * Fired whenever a visible spurt or burst event begins — i.e. any time
     * `startSpoutSpurt` or `burstPipe` actually produces visible output.
     * GameScene uses this to stop the looping blood SFX on the first leak.
     */
    private onSpurtOrBurst: () => void = () => {},
  ) {}

  startFluids(): {
    /** Count of CORRECT pipes (used by GameScene to decide "level solved"). */
    correctCount: number;
    /** Count of ACCEPTABLE pipes (flow through but don't count toward solving). */
    acceptableCount: number;
    /** Count of UNACCEPTABLE pipes (flow then burst at destination). */
    mismatchCount: number;
    emptySpouts: number;
    /** Longest first-molecule travel time across all flowing pipes (ms). */
    maxFlowTravelMs: number;
  } {
    let correctCount = 0;
    let acceptableCount = 0;
    let mismatchCount = 0;
    let emptySpouts = 0;
    let maxFlowTravelMs = 0;

    this.isFluidsRunning = true;

    const ports = this.getPorts();
    const findPort = (id: string | null) => (id ? (ports.find((p) => p.id === id) ?? null) : null);
    const pipes = this.getPipes();

    for (const pipe of pipes) {
      if (pipe.state !== 'connected') continue;
      const from = findPort(pipe.fromPortId);
      const to = findPort(pipe.toPortId);
      if (!from || !to) continue;

      const travelMs = pipe.pathLengthPx() / MOL_SPEED_PX_PER_MS;
      // maxFlowTravelMs (used by GameScene to gate the NEXT button reveal) is
      // computed at the end of this function via a DAG walk that sums travel
      // times along the longest dependency chain — not just the max of any one
      // pipe's travel. A per-pipe `travelMs` is still needed for the inline
      // arrival timers below.

      // Trachea pipe (both endpoints bidirectional): special alternating cycle,
      // gray pipe segments. TracheaFlowFx manages everything; we don't classify.
      if (pipe.specialKind === 'trachea') {
        pipe.setFlowing(true);
        // ceilingIsAtStart = the source-end port is the wall port (not on an organ).
        const fromIsOrgan = this.getOrgans().some((o) =>
          o.ports.some((p) => p.id === from.id),
        );
        const ceilingIsAtStart = !fromIsOrgan;
        const tracheaFx = new TracheaFlowFx(this.scene, pipe, ceilingIsAtStart);
        tracheaFx.start();
        this.tracheaFxByPipeId.set(pipe.id, tracheaFx);
        // Mark the trachea delivered IMMEDIATELY, not after the sprite's travel
        // time. The atmosphere is an ambient O₂ source: the instant the trachea
        // is attached, the lungs' air-side is "connected", and the gas-exchange
        // transform should treat it as present right away.
        //
        // Gating this on travelMs created a race: if the student drew a long
        // trachea, the CO₂ blood could arrive at the lungs' left input BEFORE
        // the trachea's O₂ "delivered". During that window the right-out
        // transform fell into its no-trachea PASSTHROUGH branch and emitted a
        // CO₂ molecule toward the O₂ wall receptacle (issue: stray CO₂ reaches
        // the oxygen-blood receptacle). Delivering at flow-start closes the
        // window. The decorative O₂-down/CO₂-up animation is driven separately
        // by TracheaFlowFx and is unaffected; the genuine no-trachea case still
        // never delivers lungs-top, so its passthrough→burst feedback stands.
        this.deliveredPipes.add(pipe.id);
        // Trachea pipes DON'T increment correctCount — they're a special
        // mechanic, not a spout-satisfying connection. The level requires the
        // trachea indirectly (without it, the lungs' right-out emits
        // passthrough and the downstream blood pipe classifies UNACCEPTABLE).
        continue;
      }

      // Receptacle↔receptacle: inert. No flow, no spurt — nothing pushes.
      if (from.kind === 'receptacle' && to.kind === 'receptacle') continue;

      // Spout↔spout (v5.0 change): flow normally, destination spurts on arrival.
      // The destination doesn't burst — it's just a passive leak.
      if (from.kind === 'spout' && to.kind === 'spout') {
        if (from.dependsOn.length === 0 && !from.transform) {
          this.startPipeFlow(pipe, from);
          this.scheduleArrivalSpurt(pipe, from, to, travelMs);
        }
        // Organ-output spout with a rule: deferred to update(); arrival-spurt
        // scheduled when the pipe transitions to flowing.
        continue;
      }

      // Normal spout → receptacle. Classify by declared carries (stable across
      // the cascade — each spout either emits its declared mix or nothing).
      // Note: for `transform`-form spouts, declared carries is cosmetic; the
      // per-frame update reclassifies using the runtime emission when it
      // changes. Initial classification here is still useful for the initial
      // correctCount estimate.
      const tier = classifyPipe(from.carries, to.carries, to.acceptsAlso, to.acceptsAny);
      if (tier === 'correct') correctCount++;
      else if (tier === 'acceptable') acceptableCount++;
      else mismatchCount++;

      if (from.dependsOn.length === 0 && !from.transform) {
        // Wall spout or unconditional organ spout — start flowing immediately.
        this.startPipeFlow(pipe, from);
        this.scheduleArrivalEffects(pipe, from, to, tier, travelMs);
      }
      // Else: rule-governed organ output — the per-frame update() will start
      // the flow once the rule fires.

      // validIf gating: when the destination receptacle declares `validIf` and
      // it returns false at the moment of first-molecule arrival, force a
      // burst (organ-prerequisite violated). The check happens at travelMs so
      // dependent pipes (e.g. the diaphragm pulse direct-link) have a chance
      // to deliver first.
      if (to.validIf) {
        const timer = this.scene.time.delayedCall(travelMs + 50, () => {
          const inputs = this.collectOrganInputs(to);
          if (!to.validIf!(inputs)) {
            this.burstPipe(pipe, from.carries);
          }
        });
        this.pendingValidIfTimers.set(pipe.id, timer);
      }
    }

    // Empty-spout spurt: a spout with NO pipe attached at all (neither as
    // source nor as destination) leaks its declared fluid. Spouts that are
    // the destination of a spout↔spout pipe handle their spurt via the
    // arrival timer above, so they don't double-spurt here.
    //
    // Rule-governed organ-output spouts are NOT empty-spurted here. Their
    // emission is gated by the rule — they only spurt when their rule fires,
    // which can't happen at t=0 (no delivery yet). The per-frame update()
    // starts/stops their cone-tip spurt as the rule satisfies/unsatisfies.
    for (const port of ports) {
      if (port.kind !== 'spout' && port.kind !== 'bidirectional') continue;
      if (port.dependsOn.length > 0 || port.transform) continue;
      const hasAnyPipe = pipes.some(
        (p) => p.fromPortId === port.id || p.toPortId === port.id,
      );
      if (hasAnyPipe) continue;
      // Bidirectional ports (atmosphere): sprites-only spurt — no carrier
      // fluid particles. Just floating O₂ molecules.
      const spritesOnly = port.kind === 'bidirectional';
      this.startSpoutSpurt(port, undefined, spritesOnly);
      emptySpouts++;
    }

    // Compute deepest-cascade delivery time across the DAG. The NEXT button
    // reveal uses this — for cascading levels (e.g. Level 10's gruel→stomach→
    // intestines→brain→heart chain) the actual time for all pipes to start
    // flowing is the SUM of travel times along the longest dependency chain,
    // not the max of any single pipe.
    maxFlowTravelMs = this.computeCascadeDepthMs();

    return { correctCount, acceptableCount, mismatchCount, emptySpouts, maxFlowTravelMs };
  }

  /**
   * Re-evaluates the "level solved" check based on actual runtime flow state,
   * not the t=0 declared-carries classification used by `startFluids()`.
   *
   * Why this exists: `startFluids` counts a pipe as CORRECT iff its source's
   * *declared* carries match the destination. Rule-gated spouts (dependsOn /
   * transform) classify CORRECT at t=0 even when their rule never satisfies —
   * so a pipe can be marked CORRECT and still never actually flow. That made
   * L8 falsely "solve" when the student wired the lungs without placing the
   * heart (issue #9): the lungs.right-out pipe classified CORRECT, but
   * stayed in `connected` state forever because lungs.left-in had no source.
   *
   * Called from `GameScene.toggleFluids` inside the deferred timer that
   * also reveals the NEXT button — by that point the cascade has had time
   * to fully propagate, so any pipe still in `connected` state is genuinely
   * stuck.
   *
   * Counts a pipe as CORRECT iff its state is `flowing` AND its current
   * emission (per-frame, for rule-gated spouts) matches the destination.
   * Direct-link pipes always count (the physics is the joined castellated
   * pieces; their state is set to `flowing` at creation). Trachea pipes
   * still don't count, mirroring the t=0 convention.
   */
  isLevelSolved(spoutsTotal: number): boolean {
    const pipes = this.getPipes();
    const ports = this.getPorts();
    let correctFlowing = 0;
    for (const pipe of pipes) {
      if (pipe.specialKind === 'trachea') continue;
      if (pipe.state !== 'flowing') continue;
      const from = ports.find((p) => p.id === pipe.fromPortId);
      const to = ports.find((p) => p.id === pipe.toPortId);
      if (!from || !to) continue;
      if (from.kind === 'receptacle' && to.kind === 'receptacle') continue;
      if (from.kind === 'spout' && to.kind === 'spout') continue;
      // Use runtime emission for rule-gated spouts (transform spouts can
      // emit a mix that differs from `carries`); declared carries otherwise.
      const sourceMix = this.currentSpoutEmissions.get(from.id) ?? from.carries;
      const tier = classifyPipe(sourceMix, to.carries, to.acceptsAlso, to.acceptsAny);
      if (tier === 'correct') correctFlowing++;
    }
    return correctFlowing === spoutsTotal;
  }

  /**
   * Walks the pipe dependency DAG to compute the longest "earliest-delivery"
   * time across all connected pipes. For each pipe, the earliest its first
   * molecule arrives at the destination is:
   *   - Unconditional source (wall, or organ spout with no rule): pipe.travelMs
   *   - Rule-governed organ spout: max(dep deliveries) + pipe.travelMs
   *     where deps are the incoming pipes on every input port (receptacle /
   *     bidirectional) on the SAME organ as the source spout.
   *
   * Conservative — assumes a transform needs ALL its inputs to stabilize at
   * the final emission. Some transforms emit earlier with partial inputs
   * (e.g. intestines side-out's passthrough mode), but for NEXT-button
   * gating we want to wait for the final-state cascade, not the first frame
   * a downstream pipe starts flowing.
   *
   * Memoized for performance + cycle safety (tentative value stored before
   * recursion so any cycle resolves to its own current value).
   */
  private computeCascadeDepthMs(): number {
    const pipes = this.getPipes();
    const ports = this.getPorts();
    const organs = this.getOrgans();
    const memo = new Map<string, number>();

    const compute = (pipe: Pipe): number => {
      if (memo.has(pipe.id)) return memo.get(pipe.id)!;
      const pipeTravelMs = pipe.pathLengthPx() / MOL_SPEED_PX_PER_MS;
      // Tentative value (cycle safety): if anything recurses back into this
      // pipe during the walk, it'll see this pipe-only travel time.
      memo.set(pipe.id, pipeTravelMs);

      const source = ports.find((p) => p.id === pipe.fromPortId);
      if (!source) return pipeTravelMs;

      // Unconditional source emits at t=0 — pipe delivers at its own travelMs.
      if (source.dependsOn.length === 0 && !source.transform) {
        memo.set(pipe.id, pipeTravelMs);
        return pipeTravelMs;
      }

      // Rule-governed: find the organ that owns this spout, then recurse on
      // every incoming pipe at every input port on that organ.
      const owningOrgan = organs.find((o) => o.ports.some((p) => p.id === source.id));
      if (!owningOrgan) {
        memo.set(pipe.id, pipeTravelMs);
        return pipeTravelMs;
      }

      let maxDep = 0;
      for (const inputPort of owningOrgan.ports) {
        if (inputPort.kind === 'spout') continue;
        const incoming = pipes.find(
          (p) => p.toPortId === inputPort.id && p.state === 'connected',
        );
        if (!incoming) continue;
        const depDelivery = compute(incoming);
        if (depDelivery > maxDep) maxDep = depDelivery;
      }

      const result = maxDep + pipeTravelMs;
      memo.set(pipe.id, result);
      return result;
    };

    let max = 0;
    for (const pipe of pipes) {
      if (pipe.state !== 'connected') continue;
      const d = compute(pipe);
      if (d > max) max = d;
    }
    return max;
  }

  /**
   * Per-frame rule eval. Called from `GameScene.update`. For each organ-output
   * spout with a rule (`dependsOn` or `transform`), compute its current
   * emission and reconcile the world to it:
   *   - emission null → connected (pipe gray) / no cone-tip spurt
   *   - emission non-null → flowing (pipe lit) / cone-tip spurt running
   *   - emission changed (only possible with `transform`): mid-flow
   *     `FlowFx.setCarries(newMix)` so already-traveling sprites finish their
   *     tween and new spawns use the new mix.
   *
   * The per-spout active emission is cached in `currentSpoutEmissions` so
   * we can detect transitions between frames.
   */
  update(_time: number, _delta: number): void {
    if (!this.isFluidsRunning) return;
    const ports = this.getPorts();
    const pipes = this.getPipes();
    const findPort = (id: string | null) =>
      id ? (ports.find((p) => p.id === id) ?? null) : null;

    // Spout-by-spout: only organ-output spouts (dependsOn or transform) drive
    // per-frame state. Wall spouts and unconditional organ spouts started at
    // startFluids and don't change.
    for (const spout of ports) {
      // Output-side: spouts AND bidirectional ports both emit.
      if (spout.kind === 'receptacle') continue;
      if (spout.dependsOn.length === 0 && !spout.transform) continue;

      // Bidirectional ports on a trachea pipe — TracheaFlowFx handles their
      // sprite emission directly via the alternating cycle. Skip the normal
      // pipe-flow + spurt state machine; the trachea sync at the bottom of
      // this method reads the emission separately.
      if (spout.kind === 'bidirectional') {
        const onTrachea = pipes.some(
          (p) =>
            p.specialKind === 'trachea' &&
            (p.fromPortId === spout.id || p.toPortId === spout.id),
        );
        if (onTrachea) continue;
      }

      const prevEmission = this.currentSpoutEmissions.get(spout.id) ?? null;
      const newEmission = this.getCurrentEmission(spout);

      const pipe = pipes.find((p) => p.fromPortId === spout.id);

      // Case A: emission turned ON.
      if (!prevEmission && newEmission) {
        this.currentSpoutEmissions.set(spout.id, newEmission);
        if (pipe) {
          this.startPipeFlowWithMix(pipe, spout, newEmission);
          const to = findPort(pipe.toPortId);
          const travelMs = pipe.pathLengthPx() / MOL_SPEED_PX_PER_MS;
          if (to && to.kind === 'spout') {
            this.scheduleArrivalSpurt(pipe, spout, to, travelMs);
          } else if (to) {
            const tier = classifyPipe(newEmission, to.carries, to.acceptsAlso, to.acceptsAny);
            this.scheduleArrivalEffects(pipe, spout, to, tier, travelMs);
          }
        } else {
          // No pipe attached — cone-tip spurt instead.
          const before = this.spoutSpurts.length;
          this.startSpoutSpurt(spout, newEmission);
          const added = this.spoutSpurts.slice(before);
          this.organSpoutSpurts.set(spout.id, added);
        }
        continue;
      }

      // Case B: emission turned OFF.
      if (prevEmission && !newEmission) {
        this.currentSpoutEmissions.delete(spout.id);
        if (pipe) {
          this.stopPipeFlow(pipe);
          const burstTimer = this.pendingBurstTimers.get(pipe.id);
          if (burstTimer) {
            burstTimer.remove(false);
            this.pendingBurstTimers.delete(pipe.id);
          }
          const arrivalTimer = this.pendingArrivalTimers.get(pipe.id);
          if (arrivalTimer) {
            arrivalTimer.remove(false);
            this.pendingArrivalTimers.delete(pipe.id);
          }
          this.armedPipes.delete(pipe.id);
        } else {
          // Stop the cone-tip spurt.
          const sprites = this.organSpoutSpurts.get(spout.id) ?? [];
          for (const s of sprites) {
            s.destroy();
            const i = this.spoutSpurts.indexOf(s);
            if (i !== -1) this.spoutSpurts.splice(i, 1);
          }
          this.organSpoutSpurts.delete(spout.id);
        }
        continue;
      }

      // Case C: emission stays ON but mix CHANGED (only possible with transform).
      if (prevEmission && newEmission && !this.mixesEqual(prevEmission, newEmission)) {
        this.currentSpoutEmissions.set(spout.id, newEmission);
        if (pipe) {
          // Mid-flow update: existing sprites finish, new spawns use the new mix.
          const fx = this.fxByPipeId.get(pipe.id);
          fx?.setCarries(newEmission);
          // If destination is a receptacle, re-classify with the new emission.
          const to = findPort(pipe.toPortId);
          if (to && to.kind === 'receptacle') {
            const newTier = classifyPipe(
              newEmission,
              to.carries,
              to.acceptsAlso,
              to.acceptsAny,
            );
            // If the new emission is UNACCEPTABLE and we don't already have a
            // burst scheduled, schedule one (using the pipe's travelMs from
            // NOW). Conversely, if the tier improved out of UNACCEPTABLE, the
            // old burst timer becomes incorrect — but for v5.1 use cases
            // (intestines side-out goes oxygen→enriched, both subsets of
            // mixed blood) this transition is CORRECT/ACCEPTABLE both ways,
            // so no burst handling needed. We re-arm only on degradation.
            if (newTier === 'unacceptable' && !this.pendingBurstTimers.has(pipe.id)) {
              const travelMs = pipe.pathLengthPx() / MOL_SPEED_PX_PER_MS;
              const timer = this.scene.time.delayedCall(travelMs + 100, () => {
                this.burstPipe(pipe, newEmission);
              });
              this.pendingBurstTimers.set(pipe.id, timer);
            }
          }
        } else {
          // Pipeless spout: kill the old spurt and start a fresh one with the
          // new mix. (Could be smarter, but spurts are particle emitters
          // tuned to a fixed mix, so easier to recreate.)
          const sprites = this.organSpoutSpurts.get(spout.id) ?? [];
          for (const s of sprites) {
            s.destroy();
            const i = this.spoutSpurts.indexOf(s);
            if (i !== -1) this.spoutSpurts.splice(i, 1);
          }
          const before = this.spoutSpurts.length;
          this.startSpoutSpurt(spout, newEmission);
          this.organSpoutSpurts.set(spout.id, this.spoutSpurts.slice(before));
        }
      }
      // Case D: emission stays OFF or stays the same — nothing to do.
    }

    // Organ activation per `activatesWhen` — all listed output pipes must be flowing.
    for (const organ of this.getOrgans()) {
      const aw = organ.def.activatesWhen;
      if (!aw || aw.length === 0) continue;
      const allEmitting = aw.every((portId) =>
        pipes.some((p) => p.fromPortId === portId && p.state === 'flowing'),
      );
      // organ.activate/deactivate are idempotent — safe to call every frame.
      if (allEmitting) organ.activate();
      else organ.deactivate();
    }

    // Trachea cycle sync: for each active TracheaFlowFx, tell it whether the
    // lungs (the bidirectional port at one end of the pipe with a transform
    // function) are currently emitting CO₂. The TracheaFlowFx state machine
    // uses this to decide whether to keep alternating or wait.
    for (const [pipeId, tracheaFx] of this.tracheaFxByPipeId) {
      const pipe = pipes.find((p) => p.id === pipeId);
      if (!pipe) continue;
      // Find which endpoint is the lungs (the bidirectional port with a
      // transform — the wall atmosphere port has none).
      let lungsTop: Port | null = null;
      for (const portId of [pipe.fromPortId, pipe.toPortId]) {
        if (!portId) continue;
        const p = findPort(portId);
        if (p && p.kind === 'bidirectional' && p.transform) {
          lungsTop = p;
          break;
        }
      }
      if (!lungsTop) continue;
      const emission = this.getCurrentEmission(lungsTop);
      tracheaFx.setLungsEmittingCO2(!!emission && emission.includes('co2'));
    }
  }

  /**
   * Like `startPipeFlow` but with an explicit `effectiveCarries` parameter
   * — used when the spout's emission comes from a `transform` function
   * (intestines side-out) rather than its static `port.carries`. The pipe's
   * `fluidClass` is set from the *first* emission and held for the lifetime
   * of the flow (no segment-tint flicker on mid-flow changes).
   */
  private startPipeFlowWithMix(pipe: Pipe, _from: Port, effectiveCarries: MoleculeMix): void {
    pipe.fluidClass = fluidClassFor(effectiveCarries);
    pipe.setFlowing(true);
    const fx = new FlowFx(this.scene, pipe, effectiveCarries);
    fx.start();
    this.fxByPipeId.set(pipe.id, fx);
    const travelMs = pipe.pathLengthPx() / MOL_SPEED_PX_PER_MS;
    const pipeId = pipe.id;
    const timer = this.scene.time.delayedCall(travelMs, () => {
      this.deliveredPipes.add(pipeId);
    });
    this.pendingArrivalTimers.set(`${pipeId}:delivered`, timer);
  }

  /**
   * "Input port X is currently receiving its declared carries correctly" —
   * used by per-frame rule eval. Requires:
   *   1. Incoming pipe exists.
   *   2. Incoming pipe is `flowing`.
   *   3. The first molecule has actually traveled the pipe (`deliveredPipes`).
   *   4. Either the input port has `acceptsAny: true` (wildcard), OR the
   *      upstream spout's declared carries classify as CORRECT.
   *
   * (3) is the key bit that makes the cascade have real timing — without the
   * delivery gate, an output would start emitting the instant its dep PIPE
   * went flowing, not when the molecule arrived.
   */
  private isInputDelivered(inputPort: Port): boolean {
    const pipes = this.getPipes();
    const incoming = pipes.find((p) => p.toPortId === inputPort.id);
    if (!incoming || incoming.state !== 'flowing') return false;
    if (!this.deliveredPipes.has(incoming.id)) return false;
    const upstream = this.getPorts().find((p) => p.id === incoming.fromPortId);
    if (!upstream) return false;
    const tier = classifyPipe(
      upstream.carries,
      inputPort.carries,
      inputPort.acceptsAlso,
      inputPort.acceptsAny,
    );
    return tier === 'correct';
  }

  /**
   * Build the `inputs` map an organ's `transform` / `validIf` functions receive:
   * for every receptacle (and bidirectional input-half) on the organ that owns
   * `anyPort`, include its currently-delivering mix.
   */
  private collectOrganInputs(anyPort: Port): Map<string, MoleculeMix> {
    const inputs = new Map<string, MoleculeMix>();
    const owningOrgan = this.getOrgans().find((o) =>
      o.ports.some((p) => p.id === anyPort.id),
    );
    if (!owningOrgan) return inputs;
    for (const p of owningOrgan.ports) {
      if (p.kind === 'spout') continue; // input-side only
      if (this.isInputDelivered(p)) {
        inputs.set(p.id, this.currentInputMix(p));
      }
    }
    return inputs;
  }

  /** "What's currently flowing into this input port" — for the transform function. */
  private currentInputMix(inputPort: Port): MoleculeMix {
    const pipes = this.getPipes();
    const incoming = pipes.find((p) => p.toPortId === inputPort.id);
    if (!incoming || incoming.state !== 'flowing') return [];
    if (!this.deliveredPipes.has(incoming.id)) return [];
    const upstream = this.getPorts().find((p) => p.id === incoming.fromPortId);
    if (!upstream) return [];
    return upstream.carries;
  }

  private areDepsSatisfied(spout: Port): boolean {
    const ports = this.getPorts();
    for (const depPortId of spout.dependsOn) {
      const inputPort = ports.find((p) => p.id === depPortId);
      if (!inputPort) return false;
      if (!this.isInputDelivered(inputPort)) return false;
    }
    return true;
  }

  /**
   * Compute the spout's current emission. Returns `null` when not emitting.
   *
   * Three modes, in priority order:
   *   1. `transform` function (v5.1 escape hatch — e.g. intestines side-out).
   *      Builds an input-mix map from each of THIS ORGAN's input ports that
   *      are currently delivering, then calls the function. The function
   *      decides what (if anything) to emit. The set of input ports
   *      considered is "every receptacle on the same organ as this spout."
   *   2. `dependsOn` rule (v5.0 — e.g. stomach). If all listed inputs are
   *      delivering CORRECT, emit the declared `carries`; else null.
   *   3. Unconditional (wall spouts, or organ outputs with no rule):
   *      always emits its declared `carries`.
   */
  private getCurrentEmission(spout: Port): MoleculeMix | null {
    if (spout.transform) {
      // Gather inputs from every receptacle on the SAME organ as this spout.
      const owningOrgan = this.getOrgans().find((o) =>
        o.ports.some((p) => p.id === spout.id),
      );
      const inputs = new Map<string, MoleculeMix>();
      if (owningOrgan) {
        for (const p of owningOrgan.ports) {
          if (p.kind === 'spout') continue; // input-side only (receptacle OR bidirectional)
          if (this.isInputDelivered(p)) {
            inputs.set(p.id, this.currentInputMix(p));
          }
        }
      }
      return spout.transform(inputs);
    }
    if (spout.dependsOn.length > 0) {
      return this.areDepsSatisfied(spout) ? spout.carries : null;
    }
    return spout.carries;
  }

  /** Two molecule mixes equal as sets (order-independent). */
  private mixesEqual(a: MoleculeMix, b: MoleculeMix): boolean {
    if (a.length !== b.length) return false;
    const aSet = new Set(a);
    return b.every((m) => aSet.has(m));
  }

  private startPipeFlow(pipe: Pipe, from: Port): void {
    pipe.setFlowing(true);
    const fx = new FlowFx(this.scene, pipe, from.carries);
    fx.start();
    this.fxByPipeId.set(pipe.id, fx);
    // Schedule "first molecule arrived at destination" — drives downstream
    // dep-satisfaction so the cascade has real visible timing.
    const travelMs = pipe.pathLengthPx() / MOL_SPEED_PX_PER_MS;
    const pipeId = pipe.id;
    const timer = this.scene.time.delayedCall(travelMs, () => {
      this.deliveredPipes.add(pipeId);
    });
    // Track the timer so stopFluids can clean it up; reuse pendingArrivalTimers
    // for this since they're already cleaned together.
    this.pendingArrivalTimers.set(`${pipeId}:delivered`, timer);
  }

  private stopPipeFlow(pipe: Pipe): void {
    const fx = this.fxByPipeId.get(pipe.id);
    if (fx) {
      fx.stop();
      this.fxByPipeId.delete(pipe.id);
    }
    if (pipe.state === 'flowing') pipe.setFlowing(false);
    this.deliveredPipes.delete(pipe.id);
    const deliveredTimer = this.pendingArrivalTimers.get(`${pipe.id}:delivered`);
    if (deliveredTimer) {
      deliveredTimer.remove(false);
      this.pendingArrivalTimers.delete(`${pipe.id}:delivered`);
    }
  }

  /**
   * Schedule the "first molecule arrives at the destination" downstream effect
   * for a spout→receptacle pipe: activation pulse on CORRECT pipes, burst on
   * UNACCEPTABLE, nothing on ACCEPTABLE.
   */
  private scheduleArrivalEffects(
    pipe: Pipe,
    from: Port,
    to: Port,
    tier: ReturnType<typeof classifyPipe>,
    travelMs: number,
  ): void {
    if (this.armedPipes.has(pipe.id)) return;
    this.armedPipes.add(pipe.id);
    if (tier === 'correct') {
      const activationPortId = this.getActivationPortId();
      if (activationPortId && to.id === activationPortId) {
        const timer = this.scene.time.delayedCall(travelMs + 50, () => {
          const organ = this.getOrgans().find((o) =>
            o.ports.some((p) => p.id === activationPortId),
          );
          organ?.activate();
        });
        this.pendingActivationTimers.push(timer);
      }
    } else if (tier === 'unacceptable') {
      const timer = this.scene.time.delayedCall(travelMs + 100, () => {
        this.burstPipe(pipe, from.carries);
      });
      this.pendingBurstTimers.set(pipe.id, timer);
    }
  }

  /**
   * Schedule the destination-spout spurt for a spout↔spout pipe. Fires when
   * the first molecule reaches the destination cone tip.
   */
  private scheduleArrivalSpurt(pipe: Pipe, from: Port, to: Port, travelMs: number): void {
    if (this.armedPipes.has(pipe.id)) return;
    this.armedPipes.add(pipe.id);
    const timer = this.scene.time.delayedCall(travelMs + 50, () => {
      this.startSpoutSpurt(to, from.carries);
    });
    this.pendingArrivalTimers.set(pipe.id, timer);
  }

  /** Stop fluids, returning pipes to idle gray and removing burst marks. */
  stopFluids(): void {
    this.isFluidsRunning = false;

    for (const [, fx] of this.fxByPipeId) fx.stop();
    this.fxByPipeId.clear();

    for (const [, timer] of this.pendingBurstTimers) timer.remove(false);
    this.pendingBurstTimers.clear();

    for (const [, timer] of this.pendingArrivalTimers) timer.remove(false);
    this.pendingArrivalTimers.clear();

    for (const timer of this.pendingActivationTimers) timer.remove(false);
    this.pendingActivationTimers = [];

    this.armedPipes.clear();
    this.deliveredPipes.clear();
    this.organSpoutSpurts.clear();
    this.currentSpoutEmissions.clear();

    for (const [, fx] of this.tracheaFxByPipeId) fx.stop();
    this.tracheaFxByPipeId.clear();

    for (const [, timer] of this.pendingValidIfTimers) timer.remove(false);
    this.pendingValidIfTimers.clear();

    for (const [, sprites] of this.burstSpritesByPipeId) sprites.forEach((s) => s.destroy());
    this.burstSpritesByPipeId.clear();

    this.spoutSpurts.forEach((s) => s.destroy());
    this.spoutSpurts = [];

    for (const pipe of this.getPipes()) {
      if (pipe.state === 'flowing') pipe.setFlowing(false);
      if (pipe.state === 'burst') {
        pipe.state = 'connected';
        pipe.setFlowing(false);
        pipe.dim(1);
      }
    }

    for (const organ of this.getOrgans()) organ.deactivate();
  }

  /** Clear all visual state used by this system (called when pipes are removed). */
  clearAll(): void {
    this.stopFluids();
  }

  /**
   * Trigger the burst sequence for a pipe whose first molecule has just arrived
   * at the wrong receptacle.
   */
  private burstPipe(pipe: Pipe, carries: MoleculeType[]): void {
    this.onSpurtOrBurst();
    const fx = this.fxByPipeId.get(pipe.id);
    if (fx) {
      fx.stop();
      this.fxByPipeId.delete(pipe.id);
    }
    this.pendingBurstTimers.delete(pipe.id);
    pipe.setBurst();
    pipe.dim(0.45);
    this.renderBurst(pipe, carries);
  }

  private renderBurst(pipe: Pipe, carries: MoleculeType[]): void {
    const path = pipe.pathPoints();
    if (path.length === 0) return;
    const end = path[path.length - 1];

    const xMark = this.scene.add.image(end.x, end.y, 'burst-x');
    xMark.setDepth(30);
    // Small pop-in animation for the X
    xMark.setScale(0.2);
    this.scene.tweens.add({
      targets: xMark,
      scale: 1,
      duration: 200,
      ease: 'Back.easeOut',
    });

    // Continuous spurt of the source's carrier fluid (red blood OR yellow-green gruel).
    const isGruel = fluidClassFor(carries) === 'gruel';
    const carrierTexture = isGruel ? 'mol-gruel' : 'mol-blood';
    const carrierTint = isGruel ? COLORS.flowFillGruel : COLORS.bloodSpurt;
    const blood = this.scene.add.particles(end.x, end.y, carrierTexture, {
      lifespan: 900,
      speed: { min: 120, max: 260 },
      angle: { min: 0, max: 360 },
      scale: { start: 1.3, end: 0.2 },
      alpha: { start: 1, end: 0 },
      gravityY: 420,
      quantity: 3,
      frequency: 45,
      tint: carrierTint,
    });
    blood.setDepth(29);

    // Continuous molecule spill at the wrong receptacle.
    const molEmitters: Phaser.GameObjects.GameObject[] = [];
    for (const mol of carries) {
      const e = this.scene.add.particles(end.x, end.y, MOL_TEXTURE[mol], {
        lifespan: 1000,
        speed: { min: 80, max: 200 },
        angle: { min: 0, max: 360 },
        scale: { start: 1, end: 0.7 },
        alpha: { start: 1, end: 0 },
        gravityY: 320,
        quantity: 1,
        frequency: 80,
      });
      e.setDepth(28);
      molEmitters.push(e);
    }

    // Tracked so stopFluids() / clearAll() can destroy them when the student resets.
    this.burstSpritesByPipeId.set(pipe.id, [xMark, blood, ...molEmitters]);
  }

  /**
   * Emit a continuous spurt from a spout's cone tip — used for unattached spouts
   * (no pipe at all) and for the destination spout of a spout↔spout pipe once
   * the first molecule arrives via the pipe.
   *
   * @param port       the spout to spurt from (its facing decides the emission angle).
   * @param overrideCarries optional — molecules to spurt. Defaults to `port.carries`.
   *                   Used by the spout↔spout arrival-spurt so the destination
   *                   visually spurts whatever the source pumped through.
   */
  private startSpoutSpurt(
    port: Port,
    overrideCarries?: MoleculeType[],
    spritesOnly?: boolean,
  ): void {
    const allCarries = overrideCarries ?? port.carries;
    // Filter out non-physical molecules. 'pulse' is a mechanical signal
    // (diaphragm → lungs) with no fluid representation. If nothing
    // renderable remains, suppress the spurt entirely — the diaphragm
    // emits pulse only, and a pulse-only spout (e.g. unconnected
    // diaphragm) should not visually leak anything.
    const carries = allCarries.filter((m) => m !== 'pulse');
    if (carries.length === 0) return;
    this.onSpurtOrBurst();
    const tip = gridToWorld(port.connectionCell);
    // Emission angle range based on facing direction (Phaser angles: 0=east, 90=south, 180=west, 270=north)
    let center = 0;
    switch (port.facing) {
      case 'E':
        center = 0;
        break;
      case 'S':
        center = 90;
        break;
      case 'W':
        center = 180;
        break;
      case 'N':
        center = 270;
        break;
    }
    const angleRange = { min: center - 18, max: center + 18 };
    const isGruel = fluidClassFor(carries) === 'gruel';

    // Halved speeds vs. earlier — same density, ~half the throw distance.
    // Use the gruel texture (yellow-green) for gruel spouts; blood texture + red tint for blood.
    // `spritesOnly` (used by bidirectional/atmosphere ports) skips the carrier
    // fluid layer entirely — just the molecule sprites float out.
    if (!spritesOnly) {
      const carrierTexture = isGruel ? 'mol-gruel' : 'mol-blood';
      const carrierTint = isGruel ? COLORS.flowFillGruel : COLORS.bloodSpurt;
      const carrier = this.scene.add.particles(tip.x, tip.y, carrierTexture, {
        lifespan: 1400,
        speed: { min: 190, max: 310 },
        angle: angleRange,
        scale: { start: 1.4, end: 0.4 },
        alpha: { start: 1, end: 0 },
        gravityY: 260,
        quantity: 3,
        frequency: 40,
        tint: carrierTint,
      });
      carrier.setDepth(28);
      this.spoutSpurts.push(carrier);
    }

    for (const mol of carries) {
      const e = this.scene.add.particles(tip.x, tip.y, MOL_TEXTURE[mol], {
        lifespan: 1600,
        speed: { min: 140, max: 250 },
        angle: angleRange,
        scale: { start: 1, end: 0.7 },
        alpha: { start: 1, end: 0 },
        gravityY: 240,
        quantity: 2,
        frequency: 70,
      });
      e.setDepth(27);
      this.spoutSpurts.push(e);
    }
  }
}
